import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Router, Wifi } from 'lucide-react';
import { useSophos, useUnifi } from '@/api/network';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Network landing — two separate doors, Sophos and UniFi. Their data is never combined; each card
 * only previews its own vendor's headline numbers.
 */
export default function NetworkHomePage() {
  const sophos = useSophos({ scoped: true });
  const unifi = useUnifi({ scoped: true });
  const s = sophos.data?.data?.kpis;
  const u = unifi.data?.data?.kpis;

  return (
    <DesktopOnly title="Network is available on desktop" message="Open Cosmos on a larger screen to view the network.">
      <div className="mx-auto w-full max-w-[1100px] px-4 py-5 md:px-6">
        <PageHeader title="Network" subtitle="Sophos firewalls and UniFi are separate — pick one" />
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <VendorCard to={ROUTES.network.sophos.dashboard} icon={Router} title="Sophos" subtitle="Firewalls · Sophos Central"
            loading={sophos.isLoading} configured={sophos.data?.configured}
            stats={s && [
              { label: 'Firewalls', value: s.firewalls },
              { label: 'Disconnected', value: s.disconnected, bad: s.disconnected > 0 },
              { label: 'Xstream expired', value: s.subscriptionsExpired, bad: s.subscriptionsExpired > 0 },
              { label: 'Open alerts', value: s.alerts, warn: s.alerts > 0 },
            ]}
            links={[{ to: ROUTES.network.sophos.firewalls, label: 'Firewalls' }, { to: ROUTES.network.sophos.subscriptions, label: 'Subscriptions' }, { to: ROUTES.network.sophos.alerts, label: 'Alerts' }]} />
          <VendorCard to={ROUTES.network.unifi.dashboard} icon={Wifi} title="UniFi" subtitle="Consoles, sites & devices · UniFi Site Manager"
            loading={unifi.isLoading} configured={unifi.data?.configured}
            stats={u && [
              { label: 'Consoles', value: u.consoles },
              { label: 'Disconnected', value: u.consolesDisconnected, warn: u.consolesDisconnected > 0 },
              { label: 'Devices offline', value: u.devicesOffline, bad: u.devicesOffline > 0 },
              { label: 'WAN degraded', value: u.wanDegraded, warn: u.wanDegraded > 0 },
            ]}
            links={[{ to: ROUTES.network.unifi.sites, label: 'Sites' }, { to: ROUTES.network.unifi.devices, label: 'Devices' }, { to: ROUTES.network.unifi.wan, label: 'ISP & WAN' }]} />
        </div>
      </div>
    </DesktopOnly>
  );
}

function VendorCard({ to, icon: Icon, title, subtitle, loading, configured, stats, links }: {
  to: string; icon: typeof Router; title: string; subtitle: string; loading: boolean; configured: boolean | undefined;
  stats: { label: string; value: number; bad?: boolean; warn?: boolean }[] | undefined; links: { to: string; label: string }[];
}) {
  return (
    <section className="rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <Link to={to} className="group flex items-center gap-4 border-b border-divider px-5 py-4">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-soft text-brand"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </Link>
      <div className="grid grid-cols-4 gap-2 px-5 py-4">
        {loading ? <div className="col-span-4 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
          : configured === false ? <div className="col-span-4 text-xs text-muted-foreground">Not configured on this server.</div>
          : stats?.map((s) => (
            <div key={s.label}>
              <div className={cn('text-xl font-semibold tabular-nums', s.bad ? 'text-destructive' : s.warn ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>{s.value}</div>
              <div className="text-[11px] text-muted-foreground">{s.label}</div>
            </div>
          ))}
      </div>
      <div className="flex flex-wrap gap-1.5 px-5 pb-4">
        {links.map((l) => <Link key={l.to} to={l.to} className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-ring hover:text-foreground">{l.label} →</Link>)}
      </div>
    </section>
  );
}
